import { ArrowLeft, LockKeyhole, Pause } from "lucide-react";
import type { ConversationQuestion, ParticipantResponse, Perspective } from "@samklang/schemas";
import { Button, Card } from "@samklang/ui";
import { AnswerPanel } from "./AnswerPanel";
import type { CommunicationPreferences } from "@/stores/conversation-store";
import { questionPresentation } from "@/domain/question-presentation";

interface ConversationScreenProps { readonly question: ConversationQuestion; readonly preferences: CommunicationPreferences; readonly perspective?: Perspective; readonly current: number; readonly total: number; readonly onAnswer: (response: ParticipantResponse) => void; readonly onPrevious: () => void; readonly onPause: () => void; readonly onProfessional: () => void }

export function ConversationScreen({ question, preferences, perspective, current, total, onAnswer, onPrevious, onPause, onProfessional }: ConversationScreenProps) {
  const progress = Math.round((current / total) * 100);
  const presentation = questionPresentation(question, preferences, perspective);
  const progressLabel = progress < 34 ? "I början" : progress < 67 ? "Ungefär halvvägs" : "Snart klart";

  return (
    <section className="mx-auto w-full max-w-7xl px-5 py-5 sm:px-8 lg:px-10">
      <div className="mb-7 grid items-center gap-3 sm:grid-cols-[auto_1fr_auto]">
        <Button variant="ghost" onClick={onPrevious} aria-label="Tillbaka till föregående fråga">
          <ArrowLeft size={19} /> Tillbaka
        </Button>
        <div>
          <div className="mb-2 flex items-center justify-between text-sm font-medium text-foreground-muted">
            <span>Fråga {current} av ungefär {total}</span>
            <span>{progressLabel}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-muted" aria-hidden="true">
            <div className="h-full rounded-full bg-primary transition-[width] motion-reduce:transition-none" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <Button variant="ghost" onClick={onPause}><Pause size={18} /> Paus</Button>
      </div>

      <Card padding="none" className="mx-auto min-h-[520px] w-full overflow-hidden">
        <div className="tablet-conversation-surface md:grid md:grid-cols-[minmax(15rem,.72fr)_minmax(0,1.28fr)]">
          <div className="border-b border-border/60 bg-primary-soft/35 p-7 sm:p-9 md:flex md:flex-col md:justify-between md:border-b-0 md:border-r">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-primary">{question.section}</p>
              <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                {presentation.title}
              </h1>
              {presentation.supportText ? <p className="mt-4 max-w-xl text-lg leading-relaxed text-foreground-muted">{presentation.supportText}</p> : null}
            </div>
            <div className="mt-8 hidden space-y-3 md:block">
              <div className="rounded-2xl bg-surface-raised/70 p-4 text-sm leading-relaxed text-foreground-muted">
                Ta den tid du behöver. Du kan alltid pausa, hoppa över eller säga att du inte vill svara.
              </div>
              <Button variant="ghost" fullWidth onClick={onProfessional}><LockKeyhole size={18} /> Till professionell vy</Button>
            </div>
          </div>
          <div className="flex min-w-0 flex-col justify-center p-7 sm:p-9 lg:p-10">
            <AnswerPanel key={question.id} question={question} visualSupport={presentation.visualSupport} fewerOptions={presentation.fewerOptions} onAnswer={onAnswer} />
          </div>
        </div>
      </Card>

      <div className="mt-4 flex justify-center md:hidden">
        <Button variant="ghost" onClick={onProfessional}><LockKeyhole size={18} /> Till professionell vy</Button>
      </div>
    </section>
  );
}
